import React, { useEffect, useState, useMemo } from "react";
import secureLocalStorage from "react-secure-storage";
import toast, { Toaster } from "react-hot-toast";
import useAuth from "../../stores/authStore";
import { api } from "../../utils/axios-instance";
import { auth } from "../../helper/firebaseClient";
// import { useDepartment } from "../stores/departmentStore";

const ProfilePersonalInfo = ({ isEditing, cancelled }) => {
  const profileData = useAuth((state) => state.profileData);
  const setProfileData = useAuth((state) => state.setProfileData);

  const [prevIsEditing, setPrevIsEditing] = useState(isEditing);
  const [departments, setDepartments] = useState([]);

  const rolePriv = profileData?.role_priv;
  const canSeeAllDepts = rolePriv === "org_admin" || rolePriv === "super_admin";

  // Keep department selection as a SINGLE dept id (legacy edit UX)
  const [formData, setFormData] = useState({
    firstName: profileData?.first_name || "",
    lastName: profileData?.last_name || "",
    email: profileData?.email || "",
    // phone: profileData?.phone || 0,
    // phone: profileData?.profile?.phone || profileData?.phone || "",
    phone: profileData?.contact?.phone || "",
    designation: profileData?.role_priv || "",
    departmentId:
      profileData?.dept === "All"
        ? "All"
        : profileData?.dept_ids?.[0] || profileData?.dept || "",
  });

  // Fetch org departments
  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const dep = await api.get(`/dept/listDepts`);
        setDepartments(dep.data || []);
      } catch (error) {
        console.log("Error fetching departments", error);
        setDepartments([]);
      }
    };
    fetchDepartments();
  }, []);

  // Re-hydrate form data when profileData changes (important after refresh/login)
  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      firstName: profileData?.first_name || "",
      lastName: profileData?.last_name || "",
      email: profileData?.email || "",
      // phone: profileData?.phone || 0,
      // phone: profileData?.profile?.phone || profileData?.phone || "",
      phone: profileData?.contact?.phone || "",
      designation: profileData?.role_priv || "",
      departmentId:
        profileData?.dept === "All"
          ? "All"
          : profileData?.dept_ids?.[0] || profileData?.dept || "",
    }));
  }, [profileData]);

  // Build display departments list:
  // 1) prefer profileData.dept_names
  // 2) fallback: map dept_ids using /dept/listDepts
  // 3) org_admin or dept === "All" => show ALL org departments
  const deptDisplay = useMemo(() => {
    // Admins see all org departments always
    if (canSeeAllDepts) {
      const names = (departments || []).map((d) => d?.name).filter(Boolean);
      return { names, count: names.length };
    }

    const namesFromProfile = (profileData?.dept_names || []).filter(Boolean);
    if (namesFromProfile.length) {
      return { names: namesFromProfile, count: namesFromProfile.length };
    }

    const ids = profileData?.dept_ids || [];
    if (ids.length && departments?.length) {
      const map = new Map(departments.map((d) => [d.id, d.name]));
      const names = ids.map((id) => map.get(id)).filter(Boolean);
      return { names, count: names.length };
    }

    // legacy single dept fallback (if dept is an id and departments loaded)
    if (profileData?.dept && departments?.length) {
      const one = departments.find((d) => d.id === profileData.dept);
      if (one?.name) return { names: [one.name], count: 1 };
    }

    // If user has "All" but is not admin, still show all departments
    if (profileData?.dept === "All") {
      const names = (departments || []).map((d) => d?.name).filter(Boolean);
      return { names, count: names.length };
    }

    return { names: [], count: 0 };
  }, [profileData, departments, canSeeAllDepts]);

  // Always show: "name1, name2 (count)"
  const deptDisplayText =
    deptDisplay.count > 0
      ? `${deptDisplay.names.join(", ")} (${deptDisplay.count})`
      : "Department (0)";

  const handleInputChange = (e) => {
    const { name, value } = e.target; // FIX
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Save on edit -> view transition
  useEffect(() => {
    if (prevIsEditing && !isEditing && !cancelled) {
      const updateProfile = async () => {
        try {
          const token = await auth.currentUser.getIdToken();

          const current =
            profileData ||
            JSON.parse(secureLocalStorage.getItem("profileData"));

          // department selection is legacy single-dept edit:
          // const selectedDeptId = formData.departmentId;

          // // current dept (canonical first)
          // const currentDeptId =
          //   current?.dept === "All"
          //     ? "All"
          //     : current?.dept_ids?.[0] || current?.dept || "";

          // // Only allow dept change if org_admin or super_admin (NOT employee)
          // const allowDeptChange =
          //   current?.role_priv === "org_admin" ||
          //   current?.role_priv === "super_admin";

          // const isDepartmentChanged =
          //   selectedDeptId &&
          //   selectedDeptId !== "All" &&
          //   selectedDeptId !== currentDeptId;

          // const selectedDept = departments.find((d) => d.id === selectedDeptId);

          const updateData = {
            id: current?.id,
            first_name: formData.firstName,
            last_name: formData.lastName,
            // phone: formData.phone,
            // profile: {
            //   phone: formData.phone,
            // },
            contact: {
              phone: formData.phone,
            },
            // role_priv: formData.designation,

            // ...(isDepartmentChanged
            //   ? {
            //       dept_ids: [selectedDeptId],
            //       dept_names: selectedDept?.name ? [selectedDept.name] : [],
            //     }
            //   : {}),
          };

          const res = await api.put(`/users/update-profile/`, {
            profileData: updateData,
            idToken: token,
          });

          const merged = { ...current, ...res.data };
          toast.success("Profile updated successfully");
          setProfileData(merged);
          secureLocalStorage.setItem("profileData", JSON.stringify(merged));
        } catch (error) {
          console.error("Error updating profile:", error);
          toast.error("Error updating profile");
        }
      };

      updateProfile();
    }

    setPrevIsEditing(isEditing);
  }, [
    isEditing,
    cancelled,
    prevIsEditing,
    formData.firstName,
    formData.lastName,
    formData.phone,
    profileData,
    setProfileData,
  ]);

  const FieldText = ({ text, isEditing }) => (
    <div
      className={`text-md placeholder-gray-500 p-2 rounded-md ${
        isEditing && "cursor-not-allowed"
      }`}
    >
      <div className="whitespace-normal break-words">{text || "-"}</div>
    </div>
  );

  return (
    <div className="p-4 my-4 bg-[#F7F8FA] border shadow-lg border-gray-200 w-full rounded-xl">
      <Toaster position="bottom-left" reverseOrder={false} />
      <div className="grid grid-cols-1 sm:grid-cols-3 lg:gird-cols-4 gap-4">
        <span>
          <h5 className="px-2 font-semibold">First Name</h5>
          <input
            name="firstName"
            value={formData.firstName}
            onChange={handleInputChange}
            className={`text-md  placeholder-gray-500 p-2 rounded-md ${
              isEditing ? "bg-white shadow-lg border" : "bg-inherit"
            }`}
            placeholder="First Name"
            disabled={!isEditing}
          />
        </span>

        <span>
          <h5 className="px-2 font-semibold">Last Name</h5>
          <input
            name="lastName"
            value={formData.lastName}
            onChange={handleInputChange}
            className={`text-md  placeholder-gray-500 p-2 rounded-md ${
              isEditing ? "bg-white shadow-lg border" : "bg-inherit"
            }`}
            placeholder="Last Name"
            disabled={!isEditing}
          />
        </span>

        <span>
          <h5 className="px-2 font-semibold">Email Address</h5>
          <input
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            className={`text-md  placeholder-gray-500 p-2 rounded-md bg-inherit ${
              isEditing && "cursor-not-allowed"
            }`}
            placeholder="Email Address"
            disabled={true}
          />
        </span>

        <span>
          <h5 className="px-2 font-semibold">Phone Number</h5>
          <input
            name="phone"
            value={formData.phone}
            onChange={handleInputChange}
            className={`text-md lining-nums placeholder-gray-500 p-2 rounded-md ${
              isEditing ? "bg-white shadow-lg border" : "bg-inherit"
            }`}
            placeholder="Phone Number"
            disabled={!isEditing}
          />
        </span>

        <span>
          <h5 className="px-2 font-semibold">Designation</h5>
          <input
            name="designation"
            value={formData.designation}
            onChange={handleInputChange}
            className={`text-md  placeholder-gray-500 p-2 rounded-md ${
              isEditing && "cursor-not-allowed"
            }`}
            placeholder="Designation"
            disabled={true} // disabled for employee/org_admin/super_admin
            // disabled={!isEditing || profileData?.role_priv === "employee"}
          />
        </span>

        <span>
          <h5 className="px-2 font-semibold">Department</h5>

          <FieldText isEditing={isEditing} text={deptDisplayText} />

          {/* <input
            name="department"
            value={deptDisplayText}
            readOnly
            className={`text-md  placeholder-gray-500 p-2 rounded-md ${
              isEditing ? "bg-white shadow-lg border" : "bg-inherit"
            }`}
          />       */}

          {/* {isEditing && profileData?.role_priv !== "org_admin" ? (
            <select
              name="departmentId"
              value={formData.departmentId || ""}
              onChange={handleInputChange}
              className={`text-md  placeholder-gray-500 p-2 rounded-md ${
                isEditing ? "bg-white shadow-lg border" : "bg-inherit"
              }`}
              disabled={!isEditing}
            >
              <option value="" disabled>
                Select Department
              </option>
              {departments.map((dept) => (
                <option key={dept.id} value={dept.id}>
                  {dept.name}
                </option>
              ))}
            </select>
          ) : (
            <input
              name="department"
              value={deptDisplayText} // SHOW “names (count)”
              readOnly
              className={`text-md  placeholder-gray-500 p-2 rounded-md ${
                isEditing ? "bg-white shadow-lg border" : "bg-inherit"
              }`}
              // placeholder={(userDept && userDept[0]?.name) || "Department"}
              // disabled={!isEditing}
            />
          )} */}
        </span>
      </div>
    </div>
  );
};

export default ProfilePersonalInfo;
